<template>
  <div class="edit-user-dashboard" :style="{ backgroundImage: `url(${designUrl}EditUserDashboard.png)` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('UsersFavouritesPage')">My Favourites</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="container">
      <div class="content">
        <form @submit.prevent="updateUser">
          <div class="form-group">
            <label class="form-label">Full Name:</label>
            <input v-model="form.fullname" type="text" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">Email:</label>
            <input v-model="form.email" type="email" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">Phone Number:</label>
            <input v-model="form.phone_number" type="text" class="phone-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">User Type:</label>
            <select v-model="form.user_type" class="form-input" required>
              <option disabled value="">Select</option>
              <option value="Pet Owner">Pet Owner</option>
              <option value="Pet Lover">Pet Lover</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Location</label>

            <label class="location-toggle">
              <span>Enter location manually</span>
              <input type="checkbox" v-model="manualLocation" />
            </label>

            <div v-if="manualLocation" style="margin-top: 10px;">
              <input
                class="form-input"
                v-model="manualPlace"
                type="text"
                placeholder="Enter city, place, or address"
              />
            </div>
            <div v-else style="margin-top: 10px;">
              <button class="location-btn" type="button" @click="detectLocation">
                📍 Use My Location
              </button>
              <p v-if="locationDetected" class="location-success">✅ Location detected</p>
              <p v-if="locationError" class="location-error">{{ locationError }}</p>
            </div>
          </div>

          <button type="submit" class="update-button">Save Changes</button>
          <button type="button" class="update-button" @click="goToPage('DashboardPage')">Cancel</button>
        </form>

        <div v-if="successMessage" class="success">{{ successMessage }}</div>
        <div v-if="errorMessage" class="error">{{ errorMessage }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'EditUserDashboard',
  props: ['user', 'pets'],
  data() {
    return {
      designUrl: '/background_designs/',
      form: {
        fullname: this.user.fullname,
        email: this.user.email,
        phone_number: this.user.phone_number,
        user_type: this.user.user_type,
        location: this.user.location || '',
        location_coords: this.user.location_coords || ''
      },
      successMessage: '',
      errorMessage: '',
      sidebarOpen: false,
      manualLocation: false,
      manualPlace: '',
      locationDetected: false,
      locationError: '',
      petsOpen: false,
    };
  },
  methods: {
    async updateUser() {
      try {
        if (this.manualLocation && this.manualPlace.trim()) {
          const { lat, lon } = await this.getCoordsFromPlace(this.manualPlace);
          this.form.location_coords = `${lat},${lon}`;
          this.form.location = this.manualPlace;
        }

        const response = await axios.patch(
          `${backendUrl}/api/auth/users/${this.user.id}`,
          this.form,
          { withCredentials: true }
        );
        this.successMessage = 'Profile updated successfully. To see the changes, log out';
        this.errorMessage = '';
        this.$emit('profile-updated', response.data.user);
      } catch (err) {
        this.errorMessage = err.response?.data?.error || 'Failed to update profile';
        this.successMessage = '';
      }
    },
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    detectLocation() {
      if (!navigator.geolocation) {
        this.locationError = 'Geolocation not supported on this device';
        return;
      }
      navigator.geolocation.getCurrentPosition(
        pos => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          this.form.location_coords = `${lat},${lon}`;
          this.form.location = `${lat},${lon}`;
          this.locationDetected = true;
          this.locationError = '';
        },
        err => {
          this.locationError = 'Could not detect location. Try entering manually.';
          console.error('Location error:', err.message);
        }
      );
    },
    async getCoordsFromPlace(place) {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(place)}&format=json&limit=1`
      );
      const results = await resp.json();
      if (!results.length) throw new Error('Place not found');
      const { lat, lon } = results[0];
      return { lat, lon };
    },
  },
  mounted() {
    console.log('User prop:', this.user);
    console.log('phone_number:', this.user.phone_number);
    console.log('fullname:', this.user.fullname);
    console.log('email:', this.user.email);
    console.log('user_type:', this.user.user_type);
    console.log('location:', this.user.location);
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

.edit-user-dashboard {
  min-height: 100vh;
  width: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px 40px;
}

.container {
  width: 100%;
  max-width: 375px;
  background: #ffffff;
  position: relative;
  border-radius: 24px;
  box-shadow: 0 6px 32px rgba(186, 113, 255, 0.12);
  overflow: hidden;
  border: 1px solid #f0e0ff;
}

.page-header {
  width: 100%;
  max-width: 1200px;
  margin-bottom: 50px;
  align-self: center;
}

.page-title {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
}

.page-sub {
  font-size: 14px;
  color: #aaa;
  font-weight: 500;
  margin-top: 4px;
}

.content {
  padding: 20px 22px 24px;
  background: #fff;
}

form {
  background: #fff;
}

.form-group {
  margin-bottom: 18px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #888;
  margin-bottom: 7px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.form-input,
select.form-input {
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #f0e0ff;
  border-radius: 14px;
  font-size: 15px;
  background-color: #ffffff;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
}

.form-input:focus,
select.form-input:focus,
.phone-input:focus {
  border-color: #ba71ff;
  background-color: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.form-input::placeholder {
  color: #c9b8d8;
}

.phone-container {
  display: flex;
}

.phone-input {
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #f0e0ff;
  border-radius: 14px;
  font-size: 15px;
  background-color: #fffbe8;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
}

.location-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 700;
  color: #000000;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
}

.location-toggle input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #ba71ff;
  cursor: pointer;
}

.location-btn {
  width: 100%;
  padding: 13px 14px;
  border: 1.5px solid #f0e0ff;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  background-color: #fffbe8;
  color: #4a1070;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.location-btn:hover {
  background-color: #eac7ff;
  border-color: #ba71ff;
}

.location-success {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #155724;
}

.location-error {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #721c24;
}

.update-button {
  width: 100%;
  padding: 13px 14px;
  border: none;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  margin-top: 12px;
  background: #eac7ff;
  color: #4a1070;
  box-shadow: 0 6px 16px rgba(186, 113, 255, 0.25);
  letter-spacing: 0.01em;
}

.update-button:hover {
  background: #ba71ff;
  color: #fff;
  box-shadow: 0 8px 20px rgba(186, 113, 255, 0.35);
}

.update-button:active {
  transform: scale(0.98);
}

.update-button[type="button"] {
  background: #fffbe8;
  color: #888;
  box-shadow: none;
  border: 1.5px solid #f0e0ff;
}

.update-button[type="button"]:hover {
  background: #f5f0d8;
  color: #555;
}

.success,
.error {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.4;
}

.success {
  background-color: #d4edda;
  color: #155724;
}

.error {
  background-color: #f8d7da;
  color: #721c24;
}

@media (max-width: 360px) {
  .container {
    border-radius: 0;
    box-shadow: none;
  }

  .page-title { font-size: 20px; }
  .form-label { font-size: 12px; }

  .form-input,
  .phone-input,
  select.form-input {
    font-size: 14px;
    padding: 12px 13px;
  }

  .update-button {
    font-size: 14px;
    padding: 12px;
  }

  .sidebar-menu li { font-size: 14px; }
}
</style>